import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { QueueBoardEntryView, ServicePointQueueView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  EmptyState,
  formatShortAge,
  LoadingBlock,
  Notice,
  PageHeader,
  QueueList,
  RadioGroup,
  TextField,
  useShortcut,
  type QueueListItem,
} from '@wodi/ui';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  callNext,
  priorityFlagsQuery,
  queueBoardQuery,
  queuesQuery,
  queueStep,
  removeFromQueue,
  setQueueFlags,
  transferPatient,
  type QueueStep,
} from '../api/triage.ts';
import { formatWaiting } from '../format/waiting.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { startConsultation } from '../api/consultation.ts';
import { TriagePanel } from '../triage/triage-panel.tsx';
import styles from '../triage/queue-board.module.css';
import { screen } from '../layout/screen.tsx';

// A service point's queue (docs/plans/phase1-triage.md §6.1; DESIGN.md §6.3): the list
// on the left, the selected patient's banner and work on the right. The addresses carry
// internal UUIDs only. The list refreshes every few seconds while visible.

const route = getRouteApi('/_app/queues');

const LAST_POINT_KEY = 'wodi:last-queue';

function rememberedPoint(): string | null {
  try {
    return window.localStorage.getItem(LAST_POINT_KEY);
  } catch {
    return null;
  }
}

function rememberPoint(id: string): void {
  try {
    window.localStorage.setItem(LAST_POINT_KEY, id);
  } catch {
    // Storage blocked: the chooser shows next time.
  }
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const STATE_WORDS: Partial<Record<QueueBoardEntryView['status'], string>> = {
  called: 'Called',
  in_service: 'Being seen',
};

function toItem(entry: QueueBoardEntryView): QueueListItem {
  const state = STATE_WORDS[entry.status];
  // A restricted record (a sensitivity label this user's roles aren't granted) keeps its
  // place, ticket and wait, so it is called by ticket number like any other, as on the bed
  // board; nothing about the patient shows.
  if (entry.restricted) {
    return {
      id: entry.id,
      ticket: entry.ticket,
      name: 'Restricted record',
      details: 'Call by ticket number',
      waitingMinutes: entry.waitingMinutes,
      ...(state === undefined ? {} : { state }),
      ...(entry.emergency ? { markers: ['Emergency'] } : {}),
    };
  }
  const details = `${entry.patient.sex}, ${formatShortAge(entry.patient.age, entry.patient.ageEstimated)}`;
  const markers = [
    // Casualty plan §1.1 item 5: why an emergency visit is ahead of others.
    ...(entry.emergency ? ['Emergency'] : []),
    ...(entry.category === null
      ? []
      : [
          entry.category.provisional
            ? `${entry.category.display} (provisional)`
            : entry.category.display,
        ]),
    ...entry.priorityFlags.map((f) => f.display),
  ];
  return {
    id: entry.id,
    ticket: entry.ticket,
    name: entry.patient.displayName,
    details,
    waitingMinutes: entry.waitingMinutes,
    ...(state === undefined ? {} : { state }),
    ...(markers.length === 0 ? {} : { markers }),
  };
}

function summary(point: ServicePointQueueView): string {
  if (point.waiting === 0) return 'No one waiting';
  const longest =
    point.longestWaitMinutes === null ? '' : `, longest ${formatWaiting(point.longestWaitMinutes)}`;
  return `${String(point.waiting)} waiting${longest}`;
}

/** Choosing which queue this device works from; remembered on the device (not PHI). */
function QueueChooser({ onChoose }: { onChoose: (id: string) => void }) {
  const queues = useQuery(queuesQuery);
  if (queues.isPending) return <LoadingBlock label="Loading queues" rows={3} />;
  if (queues.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void queues.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(queues.error, 'The queues could not be loaded. Try again.')}
      </Notice>
    );
  }
  if (queues.data.length === 0) {
    return (
      <EmptyState>
        No service points are set up at this facility. An administrator adds them in Settings.
      </EmptyState>
    );
  }
  return (
    <section className={screen.panel} aria-label="Queues at this facility">
      <ul className={styles.chooser}>
        {queues.data.map((q) => (
          <li key={q.id}>
            <button
              type="button"
              className={styles.chooserRow}
              aria-label={`${q.name}: ${summary(q)}`}
              onClick={() => {
                onChoose(q.id);
              }}
            >
              <span>{q.name}</span>
              <span className={styles.chooserCount}>{summary(q)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "Send to"": ends this queue place and puts the patient at the back of another queue. */
export function SendToDialog({
  entryId,
  fromPointId,
  isOpen,
  onOpenChange,
  onSent,
}: {
  entryId: string;
  fromPointId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSent: (pointName: string) => void;
}) {
  const queues = useQuery(queuesQuery);
  const [target, setTarget] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const send = useMutation({
    mutationFn: () => {
      if (target === null) throw new Error('no target');
      return transferPatient(entryId, target, note.trim());
    },
    onSuccess: () => {
      const name = queues.data?.find((q) => q.id === target)?.name ?? 'the next queue';
      setTarget(null);
      setNote('');
      onSent(name);
    },
  });
  // Consultation rooms first: triage usually sends patients there (plan §6.3).
  const options = (queues.data ?? [])
    .filter((q) => q.id !== fromPointId)
    .sort((a, b) => Number(b.kind === 'consultation') - Number(a.kind === 'consultation'));
  return (
    <Dialog title="Send patient to" isOpen={isOpen} onOpenChange={onOpenChange}>
      {queues.isPending ? <LoadingBlock label="Loading queues" rows={3} /> : null}
      {options.length === 0 && !queues.isPending ? (
        <p>No other service points are open at this facility.</p>
      ) : (
        <RadioGroup
          label="Queue"
          value={target}
          onChange={setTarget}
          options={options.map((q) => ({ id: q.id, label: q.name, description: summary(q) }))}
        />
      )}
      <TextField
        label="Note for the next room (optional)"
        description="Not shown on the waiting-area display."
        value={note}
        onChange={setNote}
      />
      {send.error === null ? null : (
        <Notice tone="error">
          {errorText(send.error, 'The patient could not be sent. Try again.')}
        </Notice>
      )}
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={target === null}
          isPending={send.isPending}
          pendingLabel="Sending…"
          onPress={() => {
            send.mutate();
          }}
        >
          Send patient
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Priority flags (plan P2): elderly, pregnant, disability and the like, from the tenant's
 * value set. A flagged patient goes ahead of others in the same category when the setting
 * is on; the flag's word is shown on the row so a move is never unexplained.
 */
function PriorityFlags({ entry, online }: { entry: QueueBoardEntryView; online: boolean }) {
  const queryClient = useQueryClient();
  const options = useQuery(priorityFlagsQuery);
  const save = useMutation({
    mutationFn: (flags: string[]) => setQueueFlags(entry.id, flags),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['queues'] }),
  });
  if (!options.isSuccess || options.data.length === 0) return null;
  const current = entry.priorityFlags.map((f) => f.code);
  return (
    <fieldset className={styles.flags}>
      <legend className={styles.flagsLegend}>Priority</legend>
      {options.data.map((f) => (
        <Checkbox
          key={f.code}
          isDisabled={!online || save.isPending}
          isSelected={current.includes(f.code)}
          onChange={(on) => {
            save.mutate(on ? [...current, f.code] : current.filter((c) => c !== f.code));
          }}
        >
          {f.display}
        </Checkbox>
      ))}
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, 'The priority could not be saved.')}</Notice>
      )}
    </fieldset>
  );
}

function SelectedPatient({
  entry,
  pointId,
  pointKind,
  mayManage,
  mayTriage,
  mayConsult,
  mayAdmit,
  onDeselect,
}: {
  entry: QueueBoardEntryView;
  pointId: string;
  pointKind: ServicePointQueueView['kind'];
  mayManage: boolean;
  mayTriage: boolean;
  mayConsult: boolean;
  mayAdmit: boolean;
  onDeselect: () => void;
}) {
  // A casualty point is triage and treatment in one place (casualty plan §5).
  const consultsHere = pointKind === 'consultation' || pointKind === 'casualty';
  const triagesHere = pointKind === 'triage' || pointKind === 'casualty';
  const queryClient = useQueryClient();
  const online = useOnline();
  const [sending, setSending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['queues'] });
  const step = useMutation({
    mutationFn: (s: QueueStep) => queueStep(entry.id, s),
    onSuccess: async (_r, s) => {
      await refresh();
      if (s === 'finish' || s === 'return') onDeselect();
      if (s === 'finish') toasts.show({ title: 'Patient finished here' });
      if (s === 'return') toasts.show({ title: 'Patient returned to the queue' });
    },
  });
  const remove = useMutation({
    mutationFn: (reason: string) => removeFromQueue(entry.id, reason),
    onSuccess: async () => {
      setRemoving(false);
      await refresh();
      onDeselect();
      toasts.show({ title: 'Patient removed from the queue' });
    },
  });
  const navigate = useNavigate();
  const [consultRequestId] = useState(() => crypto.randomUUID());
  // At a consultation room, starting is starting the consultation (consultation plan §3.3):
  // the queue place moves to "being seen" in the same transaction.
  const visitId = entry.restricted ? null : entry.visitId;
  const consult = useMutation({
    mutationFn: () =>
      startConsultation(visitId ?? '', {
        clientRequestId: consultRequestId,
        queueEntryId: entry.id,
      }),
    onSuccess: async () => {
      await refresh();
      if (visitId === null) return;
      await navigate({ to: '/visits/$visitId/consultation', params: { visitId } });
    },
  });
  const openConsultation = () => {
    if (visitId === null) return;
    if (entry.status === 'in_service' || !mayConsult) {
      void navigate({ to: '/visits/$visitId/consultation', params: { visitId } });
    } else consult.mutate();
  };
  useShortcut(
    's',
    () => {
      if (visitId !== null && consultsHere && online && (mayConsult || mayTriage))
        openConsultation();
      else if (mayManage && online && entry.status !== 'in_service') step.mutate('start');
    },
    {
      description: consultsHere ? 'Open the consultation' : 'Start seeing the patient',
    },
  );
  useShortcut(
    't',
    () => {
      if (mayManage && online) setSending(true);
    },
    { description: 'Send the patient to another queue' },
  );

  return (
    <div className={styles.work}>
      {entry.restricted ? (
        <Notice tone="info">
          Restricted record. Call this patient by ticket number. Your role may not see their record,
          so someone whose role may must see them.
        </Notice>
      ) : (
        <PatientBannerLoader patientId={entry.patientId} />
      )}
      {visitId !== null &&
      consultsHere &&
      (mayConsult || mayTriage || (entry.emergency && mayAdmit)) ? (
        <div className={styles.actions}>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={consult.isPending}
            pendingLabel="Starting…"
            onPress={openConsultation}
          >
            {entry.status === 'in_service' || !mayConsult
              ? 'Open consultation (S)'
              : 'Start consultation (S)'}
          </Button>
          {entry.emergency && mayAdmit ? (
            <Link to="/visits/$visitId/admit" params={{ visitId }} className={styles.link}>
              Admit
            </Link>
          ) : null}
        </div>
      ) : null}
      {consult.error === null ? null : (
        <Notice tone="error">
          {errorText(consult.error, 'The consultation could not be started. Try again.')}
        </Notice>
      )}
      {mayManage ? (
        <div className={styles.actions} role="toolbar" aria-label="Queue actions">
          {entry.status === 'in_service' || consultsHere ? null : (
            <Button
              isDisabled={!online}
              onPress={() => {
                step.mutate('start');
              }}
            >
              Start (S)
            </Button>
          )}
          <Button
            isDisabled={!online}
            onPress={() => {
              setSending(true);
            }}
          >
            Send to (T)
          </Button>
          {entry.status === 'called' ? (
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                step.mutate('return');
              }}
            >
              Return to queue
            </Button>
          ) : null}
          {entry.status === 'in_service' ? (
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                step.mutate('finish');
              }}
            >
              Finish here
            </Button>
          ) : (
            <Button
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setRemoving(true);
              }}
            >
              Remove from queue
            </Button>
          )}
        </div>
      ) : null}
      {step.error === null ? null : (
        <Notice tone="error">{errorText(step.error, 'That step did not work. Try again.')}</Notice>
      )}
      {entry.transferNote === null ? null : (
        <Notice tone="info">{`Note from the last room: ${entry.transferNote}`}</Notice>
      )}
      {mayManage && !entry.restricted ? <PriorityFlags entry={entry} online={online} /> : null}
      {visitId !== null && triagesHere && mayTriage ? (
        <TriagePanel
          key={visitId}
          visitId={visitId}
          onSaved={() => {
            if (mayManage) setSending(true);
          }}
        />
      ) : null}
      <SendToDialog
        entryId={entry.id}
        fromPointId={pointId}
        isOpen={sending}
        onOpenChange={setSending}
        onSent={(name) => {
          setSending(false);
          void refresh();
          onDeselect();
          toasts.show({ title: `Patient sent to ${name}` });
        }}
      />
      <ReasonDialog
        title="Remove from the queue"
        isOpen={removing}
        onOpenChange={setRemoving}
        confirmLabel="Remove from queue"
        pendingLabel="Removing…"
        isPending={remove.isPending}
        error={
          remove.error === null
            ? null
            : errorText(remove.error, 'The patient could not be removed.')
        }
        onConfirm={(reason) => {
          remove.mutate(reason);
        }}
      >
        <p>The visit stays open. The patient can be added to a queue again from Reception.</p>
      </ReasonDialog>
    </div>
  );
}

export function QueueBoardPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { servicePointId, entryId } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnline();
  const board = useQuery({
    ...queueBoardQuery(servicePointId ?? ''),
    enabled: servicePointId !== undefined,
  });

  // Reopen the queue this device last worked from.
  useEffect(() => {
    if (servicePointId !== undefined) return;
    const last = rememberedPoint();
    if (last !== null) void navigate({ to: '/queues', search: { servicePointId: last } });
  }, [servicePointId, navigate]);

  const mayManage = session !== null && can(session, 'queues.manage');
  const mayTriage = session !== null && can(session, 'clinical.read');

  const select = (id: string | undefined) => {
    void navigate({
      to: '/queues',
      search: {
        ...(servicePointId === undefined ? {} : { servicePointId }),
        ...(id === undefined ? {} : { entryId: id }),
      },
    });
  };
  const choosePoint = (id: string) => {
    rememberPoint(id);
    void navigate({ to: '/queues', search: { servicePointId: id } });
  };

  const next = useMutation({
    mutationFn: () => {
      if (servicePointId === undefined) throw new Error('no queue');
      return callNext(servicePointId);
    },
    onSuccess: async ({ entryId: called }) => {
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
      select(called);
    },
  });
  useShortcut(
    'n',
    () => {
      if (mayManage && online && !next.isPending && servicePointId !== undefined) next.mutate();
    },
    { description: 'Call the next patient', enabled: servicePointId !== undefined },
  );

  if (session === null || !can(session, 'queues.read')) {
    return (
      <>
        <PageHeader title="Queue" />
        <Notice tone="info">
          You don't have permission to see queues. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }

  if (servicePointId === undefined) {
    return (
      <>
        <PageHeader title="Queue" />
        <p>Choose the queue you are working from.</p>
        <QueueChooser onChoose={choosePoint} />
      </>
    );
  }

  const selected = board.data?.entries.find((e) => e.id === entryId);

  const pointName = board.data?.servicePoint.name ?? 'Queue';

  return (
    <div className={styles.split} data-selected={selected === undefined ? undefined : true}>
      <PageHeader
        title={pointName}
        actions={
          <Button
            variant="quiet"
            onPress={() => {
              void navigate({ to: '/queues', search: {} });
              try {
                window.localStorage.removeItem(LAST_POINT_KEY);
              } catch {
                // Nothing remembered.
              }
            }}
          >
            Change queue
          </Button>
        }
      />
      <section className={styles.queue} aria-label={`Patients waiting at ${pointName}`}>
        <p className={styles.queueSummary} role="status">
          {board.data === undefined ? '' : summary(board.data.servicePoint)}
        </p>
        {!online ? (
          <Notice tone="info">
            The queue needs a connection to Wodi. Reconnect to see who is waiting.
          </Notice>
        ) : null}
        {board.isPending ? <LoadingBlock label="Loading the queue" rows={6} /> : null}
        {board.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void board.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(board.error, 'The queue could not be loaded. Try again.')}
          </Notice>
        ) : null}
        {board.isSuccess && board.data.entries.length === 0 ? (
          <EmptyState>No patients waiting. New arrivals from reception appear here.</EmptyState>
        ) : null}
        {board.isSuccess && board.data.entries.length > 0 ? (
          <QueueList
            label={`Patients at ${board.data.servicePoint.name}`}
            items={board.data.entries.map(toItem)}
            selectedId={selected?.id ?? null}
            onSelect={select}
          />
        ) : null}
        {mayManage ? (
          <div className={styles.callNext}>
            <Button
              variant="primary"
              isDisabled={!online || board.data?.servicePoint.waiting === 0}
              isPending={next.isPending}
              pendingLabel="Calling…"
              onPress={() => {
                next.mutate();
              }}
            >
              Call next (N)
            </Button>
            {next.error === null ? null : (
              <Notice tone="error">
                {errorText(next.error, 'No patient could be called. Try again.')}
              </Notice>
            )}
          </div>
        ) : null}
      </section>
      <section className={styles.detail} aria-label="Selected patient">
        {selected === undefined ? (
          <EmptyState>Select a patient or call the next one.</EmptyState>
        ) : (
          <SelectedPatient
            key={selected.id}
            entry={selected}
            pointId={servicePointId}
            pointKind={board.data?.servicePoint.kind ?? 'other'}
            mayManage={mayManage}
            mayTriage={mayTriage}
            mayConsult={can(session, 'consultation.record')}
            mayAdmit={can(session, 'inpatient.admit')}
            onDeselect={() => {
              select(undefined);
            }}
          />
        )}
      </section>
    </div>
  );
}
